import * as _ from 'lodash-es';
import moment from 'moment';
import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import deltaToText from '../editor/utils/deltaToText';
import {
  SearchFilterTimestampAction,
  SearchFilterTimestampComparison,
  SearchIsState,
} from '../graphql/generated/graphql';
import type { SearchVariables } from '../search/SearchViewSlabSearch';

type EntityType = 'user' | 'post' | 'postState' | 'group' | 'topic';

type BaseFacet = {
  prefix: string;
  mapToFilter: (values: string[]) => SearchVariables['filter'];
  alternativePrefixes?: string[];
};

export type DateFacet = BaseFacet & {
  prefix: Lowercase<SearchFilterTimestampAction>;
  type: 'date';
  comparator: 'before' | 'after';
};

export type EntityFacet = BaseFacet & {
  type: 'entity';
  entityTypes: EntityType[];
  prependCurrentUserOnMeQuery?: boolean;
};

export type Facet = EntityFacet | DateFacet;

export const MENTION_FACET: EntityFacet = {
  type: 'entity',
  prefix: 'mention',
  alternativePrefixes: ['mentions'],
  entityTypes: ['user', 'post', 'group', 'topic'],
  prependCurrentUserOnMeQuery: true,
  mapToFilter: (ids) => ({
    mentionIds: ids,
  }),
};

export const USER_FACET: EntityFacet = {
  type: 'entity',
  prefix: 'by',
  entityTypes: ['user'],
  prependCurrentUserOnMeQuery: true,
  mapToFilter: (ids) => ({
    byIds: ids,
  }),
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const OWNER_FACET: EntityFacet = {
  type: 'entity',
  prefix: 'owner',
  alternativePrefixes: ['own', 'owned'],
  entityTypes: ['user'],
  prependCurrentUserOnMeQuery: true,
  mapToFilter: (ids) => ({
    ownerId: ids[0],
  }),
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const MAINTAINER_FACET: EntityFacet = {
  type: 'entity',
  prefix: 'maintainer',
  alternativePrefixes: ['maintain', 'maintained'],
  entityTypes: ['user'],
  prependCurrentUserOnMeQuery: true,
  mapToFilter: (ids) => ({
    ownerId: ids[0],
  }),
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const CONTRIBUTOR_FACET: EntityFacet = {
  type: 'entity',
  prefix: 'contributor',
  alternativePrefixes: [
    'contributed',
    'contribute',
    'editor',
    'author',
    'authored',
  ],
  entityTypes: ['user'],
  prependCurrentUserOnMeQuery: true,
  mapToFilter: (ids) => ({
    contributorIds: ids,
  }),
};

export const TOPIC_FACET: EntityFacet = {
  type: 'entity',
  prefix: 'in',
  entityTypes: ['topic'],
  mapToFilter: (ids) => ({
    ancestorIds: ids,
  }),
};

const stateTypeMap = _.invert({
  [SearchIsState.Archived]: 'archived',
  [SearchIsState.Expired]: 'expired',
  [SearchIsState.Draft]: 'draft',
  [SearchIsState.Public]: 'public',
  [SearchIsState.Published]: 'published',
  [SearchIsState.Verified]: 'verified',
  [SearchIsState.Template]: 'template',
} satisfies Record<SearchIsState, string>);

export const POST_STATE_FACET: EntityFacet = {
  type: 'entity',
  prefix: 'is',
  entityTypes: ['postState'],
  mapToFilter: (states) => ({
    isStates: states
      .map((state) => stateTypeMap[state] as SearchIsState | undefined)
      .filter((state): state is SearchIsState => state != null),
  }),
};

export interface SearchFacetValue {
  content: string;
  prefix: string;
  separator: string;
}

type SearchFacetOp = Op & { insert: { 'search-facet': SearchFacetValue } };

export function createSearchFacetOp(
  facet: Facet,
  content: string,
): SearchFacetOp {
  const value: SearchFacetValue = {
    content,
    prefix: facet.prefix,
    separator: getSeparator(facet),
  };

  return {
    insert: {
      'search-facet': value,
    },
  };
}

export function isSearchFacetOp(op: Op): op is SearchFacetOp {
  return typeof op.insert !== 'string' && op.insert?.['search-facet'] != null;
}

export const getSeparator = (facet: Facet) => {
  switch (facet.type) {
    case 'date':
      return facet.comparator === 'before' ? '<' : '>';
    default:
      return ':';
  }
};

const serialize = (facet: Facet, content: string) => {
  const separator = getSeparator(facet);
  return `${facet.prefix}${separator}${content}`;
};

const deserialize = (facet: Facet, value: string): SearchFacetValue => {
  const separator = getSeparator(facet);
  const [prefix = '', content = ''] = value.split(separator);
  return {
    prefix,
    separator,
    content,
  };
};

export function serializeQuery(query: Delta) {
  if (query.ops.length === 1 && query.ops[0]?.insert === '\n') return '';

  return query.ops
    .map((op) => {
      if (typeof op.insert === 'string') {
        return op.insert;
      }
      if (isSearchFacetOp(op)) {
        const { prefix, content, separator } = op.insert['search-facet'];
        const facet = findFacet(prefix, separator);
        if (facet) {
          return `[${serialize(facet, content)}]`;
        }
      }
      return '';
    })
    .join('')
    .replace(/\n/g, '');
}

export function deserializeQuery(queryString: string): Delta {
  const res = queryString.matchAll(/\[(?<facet>[^\]]+)\]/g);

  let index = 0;
  let delta = new Delta();
  [...res].forEach((match) => {
    const value = match[0];
    const innerValue = match[1];
    delta = delta.insert(queryString.slice(index, match.index));

    const facet = FACETS.find((f) => {
      const separator = getSeparator(f);
      return innerValue?.startsWith(`${f.prefix}${separator}`);
    });

    if (facet && innerValue) {
      const facetValue = deserialize(facet, innerValue);
      delta = delta.insert({ 'search-facet': facetValue });
    }

    index = (match.index ?? 0) + value.length;
  });

  delta = delta.insert(queryString.slice(index));
  return delta;
}

const timestampActionTypeMap = _.invert({
  [SearchFilterTimestampAction.Edited]: 'edited',
  [SearchFilterTimestampAction.Archived]: 'archived',
  [SearchFilterTimestampAction.Published]: 'published',
  [SearchFilterTimestampAction.Verified]: 'verified',
} satisfies Record<SearchFilterTimestampAction, DateFacet['prefix']>) as Record<
  DateFacet['prefix'],
  SearchFilterTimestampAction
>;

function dateIdsToTimestampFilterParam(
  facet: Omit<DateFacet, 'mapToFilter'>,
): (ids: string[]) => SearchVariables['filter'] {
  return (ids) => {
    const date = ids[0];

    if (date) {
      const action = timestampActionTypeMap[facet.prefix];
      const momentDate = moment.utc(date, 'YYYY-MM-DD');
      const beforeOrAfter =
        facet.comparator === 'before'
          ? SearchFilterTimestampComparison.Before
          : SearchFilterTimestampComparison.After;
      const timestamp = (
        facet.comparator === 'after'
          ? momentDate.endOf('day')
          : momentDate.startOf('day')
      ).toISOString();
      return {
        timestamps: [
          {
            beforeOrAfter,
            timestamp,
            action,
          },
        ],
      };
    }

    return {};
  };
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const DATE_FACETS: DateFacet[] = (
  [
    {
      prefix: 'edited',
      type: 'date',
      comparator: 'after',
    },
    {
      prefix: 'archived',
      type: 'date',
      comparator: 'after',
    },
    {
      prefix: 'published',
      type: 'date',
      comparator: 'after',
    },
    {
      prefix: 'verified',
      type: 'date',
      comparator: 'after',
    },
    {
      prefix: 'edited',
      type: 'date',
      comparator: 'before',
    },
    {
      prefix: 'archived',
      type: 'date',
      comparator: 'before',
    },
    {
      prefix: 'published',
      type: 'date',
      comparator: 'before',
    },
    {
      prefix: 'verified',
      type: 'date',
      comparator: 'before',
    },
  ] as const
).map((facet) => {
  return {
    mapToFilter: dateIdsToTimestampFilterParam(facet),
    ...facet,
  };
});

export function getDateFacet(
  comparator: DateFacet['comparator'],
  prefix: string,
) {
  const result = DATE_FACETS.find(
    (facet) =>
      facet.type === 'date' &&
      facet.comparator === comparator &&
      facet.prefix === prefix,
  );

  if (!result)
    throw new Error(
      `DateFacet with prefix ${prefix} and comparator ${comparator} not found`,
    );
  return result;
}

export const FACETS: Facet[] = [
  MENTION_FACET,
  USER_FACET,
  TOPIC_FACET,
  POST_STATE_FACET,
  OWNER_FACET,
  MAINTAINER_FACET,
  CONTRIBUTOR_FACET,
  ...DATE_FACETS,
];

export function findFacet(prefix: string, separator: string) {
  return FACETS.find(
    (f) =>
      (f.prefix === prefix ||
        f.alternativePrefixes?.find((p) => prefix?.startsWith(p))) &&
      getSeparator(f) === separator,
  );
}

export function parseSearchQuery(input: string) {
  const delta = deserializeQuery(input);
  const filter = delta.ops
    .filter(isSearchFacetOp)
    .map((op) => {
      const { content, separator, prefix } = op.insert['search-facet'];
      const facet = findFacet(prefix, separator);
      if (facet == null) return null;
      return facet.mapToFilter([content]);
    })
    .reduce(
      (acc, curr) =>
        _.mergeWith(acc, curr, (objValue, srcValue) => {
          if (_.isArray(objValue)) {
            return objValue.concat(srcValue);
          }
          return undefined;
        }),
      {},
    );

  return { query: deltaToText(delta).trim(), filter };
}
